import { Box, Button, Popup, Table, TableRow, TopBar } from '@sam/components';
import { TableHeader, TableKey } from '@sam/components/src/Table/Table.types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  BusinessArea,
  Offer,
  OfferState,
  Order,
  Right,
  addOrRemove,
  generateOfferCopy,
  updateSchambeckUserTableConfig,
  useData,
} from 'shared';
import { useUser } from '../../components/UserContext';
import { convertOffersIntoTableEntries } from '../../utils/order/Order.utils';
import { isUserAllowedToDo } from '../../utils/user/User.utils';
import { IconComponent, IconType } from '../../components/icons/IconComponent';

export const OfferOverview: React.FC = () => {
  const { axios, user, updateContextUser } = useUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedStates, setSelectedStates] = useState<OfferState[]>([]);
  const [filterValue, setFilterValue] = useState<string>('');

  const [selectedBusinessAreaId, setSelectedBusinessAreaId] = useState<string>();

  const { data: loadedCustomers } = useData('CUSTOMER_ALL', {
    config: { fallbackData: [] },
  });
  const { data: loadedOffers } = useData('OFFER_ALL', {
    config: { fallbackData: [] },
  });

  const { data: loadedBusinessAreas } = useData('BUSINESSAREA_ALL', {
    config: { fallbackData: [] },
  });
  const { data: loadedOrders } = useData('ORDER_ALL', {
    config: { fallbackData: [] },
  });
  const { data: loadedCustomerLocations } = useData('CUSTOMER_LOCATION_ALL', {
    config: { fallbackData: [] },
  });
  const { data: loadedCustomerContacts } = useData('CUSTOMER_USERS_ALL', {
    config: { fallbackData: [] },
  });

  const [offerToCopy, setOfferToCopy] = useState<string>();
  /**
   * Helper method to copy an existing offer
   * @param offerId id of the offer to copy
   */
  const copyOffer = useCallback(
    (offerId: string): void => {
      generateOfferCopy(axios, offerId).then(
        (generatedOffer) =>
          generatedOffer && navigate('/offer/create', { state: { offer: generatedOffer } }),
      );
    },
    [axios, navigate],
  );

  //Hook to check if a filter for the current page is present
  useEffect(() => {
    const existingFilter: { path: string; states: OfferState[]; area: string } | false = JSON.parse(
      localStorage.getItem('buttonFilter') || 'false',
    );
    if (!existingFilter) return;
    if (!existingFilter?.path || existingFilter.path !== '/offer')
      localStorage.removeItem('buttonFilter');
    else {
      setSelectedBusinessAreaId(existingFilter.area);
      setSelectedStates(existingFilter?.states);
    }
  }, []);

  /**
   * Updates the selected config and add it to the localSorage
   * @param state the selected projectState
   */
  const handleStateChange = (state: OfferState): void => {
    const existingFilter: { path: string; states: OfferState[]; area: string } | false = JSON.parse(
      localStorage.getItem('buttonFilter') || 'false',
    );
    const area: string =
      existingFilter && existingFilter.area
        ? existingFilter.area
        : selectedBusinessAreaId
          ? selectedBusinessAreaId
          : '';
    localStorage.setItem(
      'buttonFilter',
      JSON.stringify({
        path: '/offer',
        area,
        states: addOrRemove(selectedStates, state),
      }),
    );
    setSelectedStates(addOrRemove(selectedStates, state));
  };
  /**
   * Updates the selected config and add it to the localSorage
   * @param area the selected businessArea
   */
  const handleAreaSelect = useCallback(
    (area: BusinessArea): void => {
      const existingFilter: { path: string; states: OfferState[]; area: string } | false =
        JSON.parse(localStorage.getItem('buttonFilter') || 'false');
      const states: OfferState[] = existingFilter ? existingFilter.states : selectedStates;
      if (selectedBusinessAreaId === area.id) {
        localStorage.setItem(
          'buttonFilter',
          JSON.stringify({
            path: '/offer',
            area: undefined,
            states,
          }),
        );
        setSelectedBusinessAreaId(undefined);
      } else {
        localStorage.setItem(
          'buttonFilter',
          JSON.stringify({
            path: '/offer',
            area: area.id,
            states,
          }),
        );
        setSelectedBusinessAreaId(area.id);
      }
    },
    [selectedBusinessAreaId, selectedStates],
  );
  /**
   * Filter buttons for business Areas, doesnt add multiple buttons for the same name
   */
  const businessAreaFilterButtons: JSX.Element[] = useMemo((): JSX.Element[] => {
    const buttons: JSX.Element[] = [];
    const addedBusinessAreaNames: string[] = [];
    loadedBusinessAreas.forEach((area) => {
      if (addedBusinessAreaNames.includes(area.name)) return;
      else {
        buttons.push(
          <Button
            value={area.name}
            active={selectedBusinessAreaId === area.id}
            onClick={() => handleAreaSelect(area)}
          />,
        );
        addedBusinessAreaNames.push(area.name);
      }
    });
    return buttons;
  }, [handleAreaSelect, loadedBusinessAreas, selectedBusinessAreaId]);

  //Hook to set tableRows everytime the offers or the filters change. Filtering the businessAreas based on the name
  const rows: TableRow[] = useMemo((): TableRow[] => {
    let filteredOffers: Offer[] = loadedOffers;
    if (selectedStates.length > 0) {
      filteredOffers = filteredOffers.filter((offer) => selectedStates.includes(offer.offerState));
    }
    if (selectedBusinessAreaId) {
      filteredOffers = filteredOffers.filter((offer) => {
        const order: Order | undefined = loadedOrders.find((order) => order.id === offer.orderId);
        if (selectedBusinessAreaId === order?.businessAreaId) return true;

        const selectedAreaName: string | undefined = loadedBusinessAreas.find(
          (area) => area.id === selectedBusinessAreaId,
        )?.name;

        const orderAreaName: string | undefined = loadedBusinessAreas.find(
          (area) => area.id === order?.businessAreaId,
        )?.name;

        return order && selectedAreaName === orderAreaName;
      });
    }
    return convertOffersIntoTableEntries(
      filteredOffers,
      loadedOrders,
      loadedCustomers,
      loadedCustomerLocations,
      loadedCustomerContacts,
      navigate,
      setOfferToCopy,
    );
  }, [
    loadedBusinessAreas,
    loadedCustomerContacts,
    loadedCustomerLocations,
    loadedCustomers,
    loadedOffers,
    loadedOrders,
    navigate,
    selectedBusinessAreaId,
    selectedStates,
  ]);

  return (
    <>
      <TopBar title={t('pages.order.overview.topBarHeadline')} onSearch={setFilterValue}>
        {isUserAllowedToDo(user.right, Right.OFFER_CREATE) && (
          <IconComponent type={IconType.ADD} onClick={() => navigate('/offer/create')} />
        )}
      </TopBar>
      <Popup
        isOpen={!!offerToCopy}
        buttons={[
          <Button value={t('general.buttons.cancel')} onClick={() => setOfferToCopy(undefined)} />,
          <Button value={t('general.buttons.yes')} onClick={() => copyOffer(offerToCopy!)} />,
        ]}
      >
        <p>{t('pages.order.overview.copyPopup')}</p>
      </Popup>
      <Box>
        <div className="offer-overview__filter-wrapper">
          <p>{t('pages.order.overview.state')}</p>
          <div className="three-columns">
            {[
              OfferState.OFFER_ACCEPTED,
              OfferState.OFFER_DECLINED,
              OfferState.OFFER_PENDING,
              OfferState.OFFER_PREP,
            ].map((state) => (
              <Button
                active={selectedStates.includes(state)}
                value={t(`pages.order.overview.offerState.${state}`)}
                onClick={() => handleStateChange(state)}
              />
            ))}
          </div>
          <p>{t('pages.order.overview.businessArea')}</p>
          <div className="three-columns">{businessAreaFilterButtons}</div>
        </div>
      </Box>
      <Box>
        <Table
          tableConfig={user.tableConfig?.get(TableKey.OFFER_OVERVIEW)}
          updateTableConfig={(updatedHeader) => {
            updateSchambeckUserTableConfig(
              axios,
              user,
              TableKey.OFFER_OVERVIEW,
              updatedHeader,
            ).then((updatedUser) => updatedUser && updateContextUser(updatedUser));
          }}
          filterValue={filterValue}
          rows={rows}
          header={
            t('pages.order.overview.tableHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </Box>
    </>
  );
};
